// Pure fold-range computation: no DOM, no CodeMirror. Each function turns a
// source string (and, for code, a pre-extracted symbol tree) into LINE-based
// `FoldRange`s, 1-based inclusive, foldable only when endLine > startLine. The
// editor (in @slate/ui) converts these to offsets.

import { extractHeadings } from '../headings/extractHeadings';
import type { CodeSymbol } from '../symbols/types';
import type { FoldRange } from '../registry/fold';

/** Count document lines the way CodeMirror does: newlines + 1. */
function lineCount(source: string): number {
  let n = 1;
  for (let i = 0; i < source.length; i++) {
    if (source.charCodeAt(i) === 10 /* \n */) n++;
  }
  return n;
}

/**
 * Fold each markdown heading down to the line BEFORE the next heading of depth
 * <= its own (or to EOF if none follows). Computed in ONE pass over the heading
 * list. A section that is only its own heading line (endLine == startLine) is not
 * foldable and is omitted.
 */
export function markdownHeadingFolds(source: string): FoldRange[] {
  const headings = extractHeadings(source);
  if (headings.length === 0) return [];
  const lastLine = lineCount(source);
  const out: FoldRange[] = [];

  for (let i = 0; i < headings.length; i++) {
    const h = headings[i];
    // Scan forward for the next heading whose depth is shallower-or-equal: that
    // heading begins the next sibling/ancestor section, so this one ends just
    // above it. Single forward pass; the outer loop makes the whole thing O(n*k)
    // only in deeply-nested docs, and headings are few relative to text.
    let end = lastLine;
    for (let j = i + 1; j < headings.length; j++) {
      if (headings[j].depth <= h.depth) {
        end = headings[j].line - 1;
        break;
      }
    }
    if (end > h.line) out.push({ startLine: h.line, endLine: end });
  }

  return out;
}

/** Leading-whitespace width of a line (each whitespace char counts as one). */
function indentWidth(line: string): number {
  let i = 0;
  while (i < line.length) {
    const c = line.charCodeAt(i);
    if (c === 32 /* space */ || c === 9 /* tab */) i++;
    else break;
  }
  return i;
}

/** A line is blank when it has no non-whitespace content. */
function isBlank(line: string): boolean {
  for (let i = 0; i < line.length; i++) {
    const c = line.charCodeAt(i);
    if (c !== 32 && c !== 9 && c !== 13 /* \r */) return false;
  }
  return true;
}

interface OpenBlock {
  line: number; // 1-based start line
  indent: number; // leading-whitespace width
  end: number; // last descendant line seen so far
}

/**
 * Conservative single-pass indentation folds: every line folds down to the last
 * following line that is more deeply indented (blank lines are transparent and
 * never close a block). Used as a fallback for languages without a richer
 * provider. The line array is built ONCE.
 */
export function indentFolds(source: string): FoldRange[] {
  const lines = source.split('\n');
  const stack: OpenBlock[] = [];
  const out: FoldRange[] = [];

  const close = (b: OpenBlock): void => {
    if (b.end > b.line) out.push({ startLine: b.line, endLine: b.end });
    const parent = stack[stack.length - 1];
    if (parent && b.end > parent.end) parent.end = b.end;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (isBlank(line)) continue; // blanks belong to the enclosing block
    const ln = i + 1;
    const indent = indentWidth(line);
    // Close every open block that this line is NOT nested inside (indent >= ours).
    while (stack.length && stack[stack.length - 1].indent >= indent) {
      close(stack.pop() as OpenBlock);
    }
    const parent = stack[stack.length - 1];
    if (parent && ln > parent.end) parent.end = ln;
    stack.push({ line: ln, indent, end: ln });
  }

  while (stack.length) close(stack.pop() as OpenBlock);

  out.sort((a, b) => a.startLine - b.startLine || a.endLine - b.endLine);
  return out;
}

/**
 * Turn a `CodeSymbol` tree into fold ranges: every symbol that spans more than
 * one line folds from its start to its end, and children fold independently.
 * Pure; reuses the ranges the symbol extractor already computed.
 */
export function foldsFromSymbols(symbols: readonly CodeSymbol[]): FoldRange[] {
  const out: FoldRange[] = [];
  const walk = (nodes: readonly CodeSymbol[]): void => {
    for (const s of nodes) {
      if (s.endLine > s.line) out.push({ startLine: s.line, endLine: s.endLine });
      if (s.children.length) walk(s.children);
    }
  };
  walk(symbols);
  return out;
}
